import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Clock, User, ExternalLink } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useReveal } from "./Home";
import Seo from "../components/Seo";

const NewsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const ref = useReveal();
  const { t, lp } = useLang();

  const article = t.news.articles.find((a) => String(a.id) === String(id));
  const related = t.news.articles.filter((a) => String(a.id) !== String(id)).slice(0, 3);

  if (!article) {
    return (
      <div className="mx-auto max-w-[1200px] px-6 py-40 text-center">
        <h1 className="text-2xl font-bold text-neutral-900">404</h1>
        <button onClick={() => navigate(lp("/news"))} className="mt-4 text-sm font-semibold text-[#F26522]">
          {t.news.back}
        </button>
      </div>
    );
  }

  return (
    <div ref={ref}>
      <Seo title={`${article.title} | Pure Investments`} description={article.excerpt} image={article.image} />
      <section className="relative overflow-hidden bg-[#0f1a10] pb-16 pt-36">
        <div className="absolute inset-0 opacity-25">
          <img src={article.image} alt="" style={{ objectPosition: article.imagePos || "center" }} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f1a10] via-[#0f1a10]/70 to-transparent" />
        </div>
        <div className="relative mx-auto max-w-[820px] px-6">
          <div className="flex items-start justify-between gap-4">
            <button onClick={() => navigate(lp("/news"))} className="inline-flex items-center gap-2 text-sm font-medium text-neutral-300 transition-colors hover:text-[#F26522]">
              <ArrowLeft className="h-4 w-4" /> {t.news.back}
            </button>
            <span className="flex-shrink-0 rounded-full bg-[#F26522] px-3 py-1 text-xs font-semibold text-white">{article.category}</span>
          </div>
          <h1 className="mt-5 text-3xl font-extrabold leading-tight text-white lg:text-4xl">{article.title}</h1>
          <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-neutral-300">
            <span className="flex items-center gap-2">
              {article.authorImage ? (
                <img src={article.authorImage} alt={article.author} className="h-9 w-9 rounded-full object-cover ring-2 ring-[#F26522]" />
              ) : (
                <User className="h-4 w-4 text-[#F26522]" />
              )}
              {t.news.by} {article.author}
            </span>
            <span className="flex items-center gap-2"><Calendar className="h-4 w-4 text-[#F26522]" /> {article.date}</span>
            <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#F26522]" /> {article.readTime} {t.news.minRead}</span>
          </div>
        </div>
      </section>

      <article className="mx-auto max-w-[820px] px-6 py-16">
        <div className="reveal overflow-hidden rounded-2xl shadow-lg">
          <img src={article.image} alt={article.title} style={{ objectPosition: article.imagePos || "center" }} className="aspect-[16/9] w-full object-cover" />
        </div>
        <div className="reveal mt-10 space-y-6">
          <p className="text-xl font-medium leading-relaxed text-neutral-800">{article.excerpt}</p>
          {article.content.map((p, i) => (
            <p key={`${article.id}-p-${i}`} className="text-[17px] leading-[1.8] text-neutral-700">{p}</p>
          ))}
          {article.link && (
            <a
              href={article.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 rounded-md bg-[#F26522] px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#d9561a] hover:shadow-lg"
            >
              {article.linkLabel || article.link}
              <ExternalLink className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </a>
          )}
        </div>
      </article>

      <section className="bg-neutral-50 py-20">
        <div className="mx-auto max-w-[1200px] px-6">
          <h2 className="reveal text-2xl font-bold text-neutral-900">{t.news.relatedTitle}</h2>
          <div className="mt-8 grid gap-8 md:grid-cols-3">
            {related.map((n, i) => (
              <Link key={n.id} to={lp(`/news/${n.id}`)} style={{ animationDelay: `${i * 80}ms` }}
                className="reveal group overflow-hidden rounded-xl border border-neutral-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                <div className="aspect-video overflow-hidden">
                  <img src={n.image} alt={n.title} style={{ objectPosition: n.imagePos || "center" }} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <span className="text-xs font-semibold text-[#F26522]">{n.category}</span>
                  <h3 className="mt-2 text-base font-bold leading-snug text-neutral-900 transition-colors group-hover:text-[#F26522]">{n.title}</h3>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#F26522]">
                    {t.news.readMore} <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsDetail;
